<template>
  <div class="business-step">
    <!-- Read-only summary once the profile is locked (submitted / in review /
         approved). The form is only rendered while the server would accept
         an edit, mirroring the backend's PROFILE_EDITABLE_STATUSES guard. -->
    <div v-if="readOnly">
      <collapsible-section title="Legal business information" icon="tim-icons icon-bank">
        <div class="business-step__ro-grid">
          <div>
            <p class="business-step__ro-label">Legal business name</p>
            <p class="business-step__ro-value">{{ localData.legal_business_name || '-' }}</p>
          </div>
          <div>
            <p class="business-step__ro-label">Business type</p>
            <p class="business-step__ro-value">{{ businessTypeLabel }}</p>
          </div>
          <div>
            <p class="business-step__ro-label">EIN or tax ID</p>
            <p class="business-step__ro-value">{{ localData.ein || '-' }}</p>
          </div>
        </div>
      </collapsible-section>

      <collapsible-section title="Business address" icon="tim-icons icon-square-pin">
        <p class="business-step__ro-value mb-0">
          {{ localData.address_line_1 }}<span v-if="localData.address_line_2">, {{ localData.address_line_2 }}</span><br>
          {{ localData.city }}, {{ localData.state }} {{ localData.zip }}
        </p>
      </collapsible-section>

      <collapsible-section title="Online presence" icon="tim-icons icon-world">
        <div class="business-step__ro-grid">
          <div>
            <p class="business-step__ro-label">Website</p>
            <p class="business-step__ro-value">{{ localData.website || '-' }}</p>
          </div>
          <div>
            <p class="business-step__ro-label">Industry</p>
            <p class="business-step__ro-value">{{ industryLabel }}</p>
          </div>
        </div>
      </collapsible-section>

      <collapsible-section title="Authorized representative" icon="tim-icons icon-single-02">
        <div class="business-step__ro-grid">
          <div>
            <p class="business-step__ro-label">Name</p>
            <p class="business-step__ro-value">{{ localData.rep_first_name }} {{ localData.rep_last_name }}<span v-if="localData.rep_title">, {{ localData.rep_title }}</span></p>
          </div>
          <div>
            <p class="business-step__ro-label">Email</p>
            <p class="business-step__ro-value">{{ localData.rep_email || '-' }}</p>
          </div>
          <div>
            <p class="business-step__ro-label">Phone</p>
            <p class="business-step__ro-value">{{ localData.rep_phone || '-' }}</p>
          </div>
        </div>
      </collapsible-section>

      <collapsible-section title="Messaging practices" icon="tim-icons icon-chat-33">
        <p class="business-step__ro-label">How customers opt in</p>
        <p class="business-step__ro-value business-step__ro-block">{{ localData.opt_in_description || '-' }}</p>
        <p class="business-step__ro-label">Sample messages</p>
        <p v-for="n in [1, 2, 3]" :key="'ro-sample-' + n" class="business-step__ro-value business-step__ro-block" v-show="localData['sample_message_' + n]">
          {{ localData['sample_message_' + n] }}
        </p>
      </collapsible-section>
    </div>

    <div v-else>
    <collapsible-section title="Legal business information" icon="tim-icons icon-bank">

      <div class="row">
        <div class="col-lg-8">
          <base-input
            label="Legal business name"
            placeholder="As registered with the IRS"
            :error="fieldErrors.legal_business_name"
            v-model="localData.legal_business_name"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-5">
          <base-input label="Business type" :error="fieldErrors.business_type">
            <el-select v-model="localData.business_type" class="select-primary" placeholder="Select a type">
              <el-option
                v-for="type in businessTypes"
                :key="type.value"
                :label="type.label"
                :value="type.value"
              />
            </el-select>
          </base-input>
        </div>
        <div class="col-lg-4">
          <base-input
            label="EIN or tax ID"
            placeholder="XX-XXXXXXX"
            :error="fieldErrors.ein"
            v-model="localData.ein"
          />
        </div>
      </div>
    </collapsible-section>

    <collapsible-section title="Business address" icon="tim-icons icon-square-pin">

      <div class="row">
        <div class="col-lg-8">
          <base-input
            label="Street address"
            placeholder="Street address"
            :error="fieldErrors.address_line_1"
            v-model="localData.address_line_1"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-8">
          <base-input
            label="Apartment, suite, etc. (optional)"
            placeholder="Suite 200"
            :error="fieldErrors.address_line_2"
            v-model="localData.address_line_2"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-4">
          <base-input
            label="City"
            placeholder="City"
            :error="fieldErrors.city"
            v-model="localData.city"
          />
        </div>
        <div class="col-lg-3">
          <base-input label="State" :error="fieldErrors.state">
            <el-select v-model="localData.state" class="select-primary" placeholder="Select" filterable>
              <el-option
                v-for="s in states"
                :key="s.code"
                :label="s.name"
                :value="s.code"
              />
            </el-select>
          </base-input>
        </div>
        <div class="col-lg-2">
          <base-input
            label="Zip"
            placeholder="Zip"
            :error="fieldErrors.zip"
            v-model="localData.zip"
          />
        </div>
      </div>
    </collapsible-section>

    <collapsible-section title="Online presence" icon="tim-icons icon-world">

      <div class="row">
        <div class="col-lg-8">
          <base-input
            label="Website"
            placeholder="https://www.yourbusiness.com"
            :error="fieldErrors.website"
            v-model="localData.website"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-6">
          <base-input label="Industry" :error="fieldErrors.industry">
            <el-select v-model="localData.industry" class="select-primary" placeholder="Select an industry" filterable>
              <el-option
                v-for="ind in industries"
                :key="ind.value"
                :label="ind.label"
                :value="ind.value"
              />
            </el-select>
          </base-input>
        </div>
      </div>
    </collapsible-section>

    <collapsible-section
      title="Authorized representative"
      icon="tim-icons icon-single-02"
      description="The person legally authorized to act on behalf of your business. Carriers may contact them to verify your registration."
    >

      <div class="row">
        <div class="col-lg-4">
          <base-input
            label="First name"
            placeholder="First name"
            :error="fieldErrors.rep_first_name"
            v-model="localData.rep_first_name"
          />
        </div>
        <div class="col-lg-4">
          <base-input
            label="Last name"
            placeholder="Last name"
            :error="fieldErrors.rep_last_name"
            v-model="localData.rep_last_name"
          />
        </div>
        <div class="col-lg-4">
          <base-input
            label="Title"
            placeholder="Owner, Director, etc."
            :error="fieldErrors.rep_title"
            v-model="localData.rep_title"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-lg-5">
          <base-input
            label="Email"
            type="email"
            placeholder="name@example.com"
            :error="fieldErrors.rep_email"
            v-model="localData.rep_email"
          />
        </div>
        <div class="col-lg-4">
          <base-input label="Phone" :error="fieldErrors.rep_phone">
            <phone-number-input
              v-model="localData.rep_phone"
              default-country="US"
            />
          </base-input>
        </div>
      </div>
    </collapsible-section>

    <collapsible-section title="Messaging practices" icon="tim-icons icon-chat-33">
      <info-hint icon="icon-badge" variant="primary">
        Carrier reviewers read this section word for word. Concrete descriptions and realistic samples get approved faster; vague ones get rejected.
      </info-hint>

      <div class="row">
        <div class="col-lg-10">
          <base-input
            label="How do customers opt in to receive messages?"
            :error="fieldErrors.opt_in_description"
          >
            <base-textarea
              :rows="3"
              :maxlength="2048"
              placeholder="Example: Customers check a consent box next to the phone field on our booking form agreeing to receive booking confirmations and reminders by text."
              v-model="localData.opt_in_description"
            />
          </base-input>
          <div class="business-step__field-footer">
            <p class="business-step__hint">
              Describe every place on your site or at your location where a customer agrees to receive texts.
            </p>
            <a href="#" class="business-step__suggest" @click.prevent="applySuggestion('opt_in_description')">Use suggested text</a>
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-lg-10">
          <p class="business-step__subheading">Sample messages</p>
          <p class="business-step__hint mb-3">
            Three examples of the kinds of messages you will send. Include opt-out instructions ("Reply STOP to unsubscribe") in at least one sample.
          </p>

          <base-input label="Sample 1: booking confirmation (required)" :error="fieldErrors.sample_message_1">
            <base-textarea
              :rows="2"
              :maxlength="1024"
              placeholder="e.g. a confirmation with your business name, the date and time, and opt-out language"
              v-model="localData.sample_message_1"
            />
          </base-input>
          <div class="business-step__field-footer business-step__field-footer--tight">
            <a href="#" class="business-step__suggest" @click.prevent="applySuggestion('sample_message_1')">Use suggested text</a>
          </div>

          <base-input label="Sample 2: booking reminder (recommended)" :error="fieldErrors.sample_message_2">
            <base-textarea
              :rows="2"
              :maxlength="1024"
              placeholder="e.g. a reminder sent a few hours before the booking"
              v-model="localData.sample_message_2"
            />
          </base-input>
          <div class="business-step__field-footer business-step__field-footer--tight">
            <a href="#" class="business-step__suggest" @click.prevent="applySuggestion('sample_message_2')">Use suggested text</a>
          </div>

          <base-input label="Sample 3: marketing message (optional)" :error="fieldErrors.sample_message_3">
            <base-textarea
              :rows="2"
              :maxlength="1024"
              placeholder="e.g. a promotional text, only sent to customers who opted into marketing"
              v-model="localData.sample_message_3"
            />
          </base-input>
          <div class="business-step__field-footer business-step__field-footer--tight">
            <a href="#" class="business-step__suggest" @click.prevent="applySuggestion('sample_message_3')">Use suggested text</a>
          </div>
        </div>
      </div>
    </collapsible-section>
    </div>
  </div>
</template>

<script>
import { Select, Option } from 'element-ui';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';
import BaseTextarea from '@/components/Inputs/BaseTextarea.vue';
import InfoHint from '@/components/InfoHint.vue';
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection';
import states from '@/assets/states.json';

export default {
  name: 'SMSOnboardingStepBusiness',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    PhoneNumberInput,
    BaseTextarea,
    InfoHint,
    CollapsibleSection
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      default: null
    },
    validationErrors: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    // Mirrors the backend's PROFILE_EDITABLE_STATUSES: past submission the
    // server rejects edits, so the form renders as a read-only summary.
    readOnly() {
      const status = this.settings?.registration_status || 'not_started';
      return !['not_started', 'profile_pending', 'brand_rejected'].includes(status);
    },
    businessTypeLabel() {
      const match = this.businessTypes.find((t) => t.value === this.localData.business_type);
      return match ? match.label : (this.localData.business_type || '-');
    },
    industryLabel() {
      const match = this.industries.find((i) => i.value === this.localData.industry);
      return match ? match.label : (this.localData.industry || '-');
    },
    // Carrier-shaped starter copy, personalized with the business name from
    // the form. Inserted via the "Use suggested text" links; always editable.
    suggestedTexts() {
      const name = (this.localData.legal_business_name || 'Our venue').trim();
      return {
        opt_in_description: 'Customers check a consent box next to the phone number field on our online booking form agreeing to receive booking confirmations and reminders by text. The checkbox is optional and unchecked by default. Guests can also opt in when signing our digital waiver. We only text customers who have a booking or purchase with us.',
        sample_message_1: `${name}: your booking for this Friday at 7:00 PM is confirmed for 6 players. Questions? Just reply to this text. Reply STOP to opt out.`,
        sample_message_2: `${name}: reminder, your game starts in 2 hours. Please arrive 15 minutes early to check in. Reply STOP to opt out.`,
        sample_message_3: `${name}: thanks for playing! Book your next visit this month and save 15% with code REMATCH. Reply STOP to opt out.`
      };
    },
    fieldErrors() {
      const map = {};
      for (const e of this.validationErrors) {
        if (e && e.field && !map[e.field]) {
          map[e.field] = e.message || 'Invalid';
        }
      }
      return map;
    }
  },
  data() {
    const defaults = this.buildDefaults();
    return {
      localData: { ...defaults, ...(this.formData || {}) },
      businessTypes: [
        { value: 'sole_proprietorship', label: 'Sole proprietorship' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'llc', label: 'Limited liability corporation' },
        { value: 'cooperative', label: 'Cooperative' },
        { value: 'non_profit', label: 'Non-profit corporation' },
        { value: 'corporation', label: 'Corporation' }
      ],
      industries: [
        { value: 'agriculture', label: 'Agriculture' },
        { value: 'automotive', label: 'Automotive' },
        { value: 'banking', label: 'Banking' },
        { value: 'construction', label: 'Construction' },
        { value: 'consumer', label: 'Consumer' },
        { value: 'education', label: 'Education' },
        { value: 'energy', label: 'Energy' },
        { value: 'engineering', label: 'Engineering' },
        { value: 'entertainment', label: 'Entertainment' },
        { value: 'fast_moving_consumer_goods', label: 'Consumer goods' },
        { value: 'financial', label: 'Financial services' },
        { value: 'government', label: 'Government' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'hospitality', label: 'Hospitality' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'legal', label: 'Legal' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'media', label: 'Media' },
        { value: 'online', label: 'Online services' },
        { value: 'professional_services', label: 'Professional services' },
        { value: 'raw_materials', label: 'Raw materials' },
        { value: 'real_estate', label: 'Real estate' },
        { value: 'religion', label: 'Religion' },
        { value: 'retail', label: 'Retail' },
        { value: 'technology', label: 'Technology' },
        { value: 'telecommunications', label: 'Telecommunications' },
        { value: 'transportation', label: 'Transportation' },
        { value: 'travel', label: 'Travel' }
      ],
      states: states.states
    };
  },
  watch: {
    localData: {
      deep: true,
      handler(newVal) {
        this.$emit('update', { ...newVal });
      }
    }
  },
  methods: {
    buildDefaults() {
      return {
        legal_business_name: '',
        business_type: '',
        ein: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
        website: '',
        industry: '',
        rep_first_name: '',
        rep_last_name: '',
        rep_title: '',
        rep_email: '',
        rep_phone: '',
        opt_in_description: '',
        sample_message_1: '',
        sample_message_2: '',
        sample_message_3: ''
      };
    },
    applySuggestion(field) {
      this.localData[field] = this.suggestedTexts[field];
    }
  }
};
</script>

<style lang="scss" scoped>
.business-step {
  // Read-only summary (profile locked after submission)
  &__ro-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px 24px;
  }

  &__ro-label {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 2px;
  }

  &__ro-value {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.5;
    margin-bottom: 0.5rem;
  }

  &__ro-block {
    white-space: pre-wrap;
    background: rgba(125, 125, 125, 0.08);
    border-radius: 0.375rem;
    padding: 10px 14px;
  }

  // Pink mini-caps sub-block heading, same pattern as the POS editor's
  // device-section titles.
  &__subheading {
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
    color: #e14eca;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__field-footer {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;

    &--tight {
      justify-content: flex-end;
      margin-top: -6px;
      margin-bottom: 10px;
    }
  }

  &__suggest {
    flex-shrink: 0;
    font-size: 0.75rem;
    color: #e14eca;
    text-decoration: none;

    &:hover {
      color: #e14eca;
      text-decoration: underline;
    }
  }

  &__hint {
    color: rgba(255, 255, 255, 0.65);
    font-size: 0.75rem;
    line-height: 1.4;
    margin-top: 0.25rem;
  }
}
</style>

<style>
.white-content .business-step__hint { color: rgba(0, 0, 0, 0.5); }
.white-content .business-step__ro-label { color: rgba(0, 0, 0, 0.55); }
.white-content .business-step__ro-value { color: rgba(0, 0, 0, 0.9); }
</style>
